import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CircleNotificationsIcon from '@mui/icons-material/CircleNotifications';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Grid, Select, MenuItem, FormControl, InputLabel, Box, Menu, Divider, ListItemIcon, ListItemText, Backdrop, CircularProgress, Dialog, DialogTitle, DialogContent, IconButton, Badge, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserActivitiesModern } from '../services/authService';
import { fetchWithAuth } from '../services/authService';
import { API_BASE_URL } from '../services/apiConfig';
import Profile from '../pages/Profile';
import Support from '../pages/Support';
import '../App.css';

interface Activity {
    activityId: string;
    indirizzo: string;
    citta: string;
    cap: string;
    provincia: string;
    telefono?: string;
    email?: string;
}

interface User {
    userId: string;
    email: string;
    ragione_sociale?: string;
}

interface CommonLayoutProps {
    user: string;
    userAuthenticated: string;
    userRole?: string;
    onLogout: () => void;
}
const COLOR = "#20679F"

export const Header: React.FC<CommonLayoutProps> = ({ userAuthenticated, userRole, onLogout }) => {
    const navigate = useNavigate();
    const [activities, setActivities] = useState<Activity[]>([]);
    const [selectedActivity, setSelectedActivity] = useState<string>("");
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [openUserDialog, setOpenUserDialog] = useState<'profile' | 'support' | null>(null);
    const openMenu = Boolean(anchorEl);
    
    // Admin dropdowns state
    const [users, setUsers] = useState<User[]>([]);
    const [selectedUserId, setSelectedUserId] = useState<string>("");
    const [userActivities, setUserActivities] = useState<Activity[]>([]);
    const [selectedUserActivity, setSelectedUserActivity] = useState<string>("");
    const isAdmin = userRole === 'Admin';
    const [supportCounts, setSupportCounts] = useState({ open: 0, inProgress: 0 });

    const currentDate = new Date().toLocaleDateString('it-IT', {
        weekday: 'long',
        day: 'numeric',
        month: 'long'
    });

    useEffect(() => {
        if (!isAdmin) return;

        const loadSupportCounts = async () => {
            try {
                const response = await fetchWithAuth(`${API_BASE_URL}/admin/support?status=all&priority=all&category=all`);
                if (!response.ok) return;
                const data = await response.json();
                const requests = data.requests || [];
                setSupportCounts({
                    open: requests.filter((request: { status?: string }) => request.status === 'OPEN').length,
                    inProgress: requests.filter((request: { status?: string }) => request.status === 'IN_PROGRESS').length
                });
            } catch (error) {
                console.error('Errore caricamento notifiche supporto:', error);
            }
        };

        loadSupportCounts();
        const intervalId = window.setInterval(loadSupportCounts, 60 * 60 * 1000);
        return () => window.clearInterval(intervalId);
    }, [isAdmin]);

    const splittedDate = currentDate.split(" ");
    const currentDayOfWeek = splittedDate[0].charAt(0).toUpperCase() + splittedDate[0].slice(1);
    const currentDayOfMonth = splittedDate[1] + " " + splittedDate[2];

    let loggedUser = "unknown";
    let ragioneSociale = "";
    let partitaIva = "";
    
    if(sessionStorage.getItem('user') !== null){
        const userData = JSON.parse(sessionStorage.getItem('user') ?? "{}");
        loggedUser = userData.email || "unknown";
        ragioneSociale = userData.ragione_sociale || "";
        partitaIva = userData.partita_iva || "";
        
        console.log('📋 Dati utente caricati:', { loggedUser, ragioneSociale, partitaIva });
    }
    
    // Ripristina selectedUserId da sessionStorage al mount
    useEffect(() => {
        if (isAdmin) {
            const savedUserId = sessionStorage.getItem('selectedUserId');
            if (savedUserId) {
                console.log('🔄 [Header] Ripristino selectedUserId da sessionStorage:', savedUserId);
                setSelectedUserId(savedUserId);
            }
        }
    }, [isAdmin]);
    
    // Carica lista utenti per admin
    useEffect(() => {
        const loadUsers = async () => {
            if (!isAdmin) return;
            
            try {
                const response = await fetchWithAuth(`${API_BASE_URL}/admin/users`);
                
                if (response.ok) {
                    const data = await response.json();
                    setUsers(data.users || []);
                }
            } catch (error) {
                console.error('Errore caricamento utenti:', error);
            }
        };
        
        loadUsers();
    }, [isAdmin]);
    
    // Carica attività dell'utente selezionato (per admin)
    useEffect(() => {
        const loadUserActivities = async () => {
            if (!isAdmin || !selectedUserId) {
                setUserActivities([]);
                setSelectedUserActivity("");
                sessionStorage.removeItem('selectedActivityId');
                return;
            }
            
            try {
                const response = await fetchWithAuth(`${API_BASE_URL}/user/activities?userId=${selectedUserId}`);
                
                if (response.ok) {
                    const data = await response.json();
                    setUserActivities(data.activities || []);
                    if (data.activities && data.activities.length > 0) {
                        // Prova a ripristinare l'attività salvata in sessionStorage
                        const savedActivityId = sessionStorage.getItem('selectedActivityId');
                        
                        // Se c'è un'attività salvata E appartiene a questo utente, ripristinala
                        const activityToSelect = (savedActivityId && 
                            data.activities.find((a: any) => a.activityId === savedActivityId))
                            ? savedActivityId
                            : data.activities[0].activityId;
                        
                        setSelectedUserActivity(activityToSelect);
                        sessionStorage.setItem('selectedActivityId', activityToSelect);
                        
                        // Emetti evento per notificare altri componenti
                        window.dispatchEvent(new CustomEvent('activityChanged', { detail: activityToSelect }));
                        
                        if (savedActivityId && activityToSelect === savedActivityId) {
                            console.log('✅ [Header] Ripristinata attività utente da sessionStorage:', savedActivityId);
                        }
                    } else {
                        // Resetta se utente non ha attività
                        setSelectedUserActivity("");
                        sessionStorage.removeItem('selectedActivityId');
                    }
                }
            } catch (error) {
                console.error('Errore caricamento attività utente:', error);
            }
        };
        
        loadUserActivities();
    }, [isAdmin, selectedUserId]);

    // Carica attività al mount del componente
    useEffect(() => {
        const loadActivities = async () => {
            if (!userAuthenticated || isAdmin) return; // Admin usa le dropdown specifiche

            window.dispatchEvent(new CustomEvent('activitiesLoading', { detail: true }));
            try {
                for (let attempt = 0; attempt < 5; attempt += 1) {
                    const data = await getUserActivitiesModern();

                    if (data.activities && Array.isArray(data.activities)) {
                        setActivities(data.activities);
                        sessionStorage.setItem('activities', JSON.stringify(data.activities));
                        if (data.activities.length > 0) {
                            const firstActivityId = data.activities[0].activityId;
                            setSelectedActivity(firstActivityId);
                            sessionStorage.setItem('selectedActivityId', firstActivityId);
                            window.dispatchEvent(new CustomEvent('activityChanged', { detail: firstActivityId }));
                            return;
                        }
                    }

                    if (attempt < 4) {
                        await new Promise(resolve => setTimeout(resolve, 2000));
                    }
                }
            } catch (error) {
                console.error("Errore caricamento attività:", error);
            } finally {
                window.dispatchEvent(new CustomEvent('activitiesLoading', { detail: false }));
            }
        };
        
        loadActivities();
    }, [userAuthenticated]);

    const handleActivityChange = (activityId: string) => {
        setSelectedActivity(activityId);
        sessionStorage.setItem('selectedActivityId', activityId);
        // Puoi anche emettere un evento personalizzato per notificare altri componenti
        window.dispatchEvent(new CustomEvent('activityChanged', { detail: activityId }));
    };

    // Handler per quando l'admin seleziona un utente
    const handleUserChange = (userId: string) => {
        setSelectedUserId(userId);
        // Salva l'userId selezionato per filtrare le attività
        if (userId) {
            sessionStorage.setItem('selectedUserId', userId);
        } else {
            sessionStorage.removeItem('selectedUserId');
            sessionStorage.removeItem('selectedActivityId');
        }
        // Emetti evento per notificare altri componenti
        window.dispatchEvent(new CustomEvent('userChanged', { detail: userId }));
    };

    // Handler per quando l'admin seleziona l'attività dell'utente
    const handleUserActivityChange = (activityId: string) => {
        setSelectedUserActivity(activityId);
        // Salva l'attività selezionata
        if (activityId) {
            sessionStorage.setItem('selectedActivityId', activityId);
        } else {
            sessionStorage.removeItem('selectedActivityId');
        }
        // Emetti evento per notificare altri componenti
        window.dispatchEvent(new CustomEvent('activityChanged', { detail: activityId }));
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = async () => {
        setIsLoggingOut(true);
        handleMenuClose();
        try {
            await onLogout();
        } finally {
            setIsLoggingOut(false);
        }
    };

    return (
        <>
        <Grid container style={{ padding: "8px 24px", backgroundColor: COLOR }} alignItems="center" spacing={2}>
            <Grid item xs={3} style={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
                <div style={{
                    width: "160px",
                    height: "60px",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: '6px 10px'
                }}>
                    <img 
                        src="/logo-app-transparent.png"
                        alt="MAMADOC" 
                        style={{ 
                            width: "200px",
                            height: "auto",
                            objectFit: "cover",
                            transform: "scale(1.15)",
                            transformOrigin: "center center",
                            filter: 'contrast(1.15) brightness(1.05)'
                        }}
                    />
                </div>
            </Grid>
            <Grid item xs={6} style={{ display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 0.5 }}>
                {/* Admin: Label e Dropdowns */}
                {isAdmin && (
                    <>
                        <Box sx={{ display: 'flex', gap: 2, width: '100%', maxWidth: 800, mb: 1 }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" sx={{ color: 'white', fontWeight: 600, mb: 0.5, display: 'block' }}>
                                    Seleziona Utente
                                </Typography>
                                <FormControl size="small" fullWidth sx={{ backgroundColor: 'white', borderRadius: 1.5 }}>
                                    <Select
                                        value={selectedUserId}
                                        onChange={(e) => handleUserChange(e.target.value)}
                                        disabled={users.length === 0}
                                        displayEmpty
                                        renderValue={(value) => {
                                            if (!value) {
                                                if (users.length === 0) {
                                                    return <Typography variant="body2" color="text.secondary">Nessun utente disponibile</Typography>;
                                                }
                                                return <Typography variant="body2" color="text.secondary">Seleziona utente</Typography>;
                                            }
                                            const user = users.find(u => u.userId === value);
                                            return (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <AccountCircleIcon sx={{ color: '#8B6F47', fontSize: '18px' }} />
                                                    <Typography variant="body2" fontWeight="600" sx={{ fontSize: '0.85rem' }}>
                                                        {user?.ragione_sociale || user?.email}
                                                    </Typography>
                                                </Box>
                                            );
                                        }}
                                    >
                                        {users.length === 0 ? (
                                            <MenuItem value="" disabled>Nessun utente</MenuItem>
                                        ) : (
                                            users.map((user) => (
                                                <MenuItem key={user.userId} value={user.userId}>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="600">
                                                            {user.ragione_sociale || user.email}
                                                        </Typography>
                                                    </Box>
                                                </MenuItem>
                                            ))
                                        )}
                                    </Select>
                                </FormControl>
                            </Box>
                            
                            {selectedUserId && (
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" sx={{ color: 'white', fontWeight: 600, mb: 0.5, display: 'block' }}>
                                        Attività Utente
                                    </Typography>
                                    <FormControl size="small" fullWidth sx={{ backgroundColor: 'white', borderRadius: 1.5 }}>
                                        <Select
                                            value={selectedUserActivity}
                                            onChange={(e) => handleUserActivityChange(e.target.value)}
                                            disabled={userActivities.length === 0}
                                            displayEmpty
                                            renderValue={(value) => {
                                                if (!value) {
                                                    if (userActivities.length === 0) {
                                                        return <Typography variant="body2" color="text.secondary">Nessuna attività disponibile</Typography>;
                                                    }
                                                    return <Typography variant="body2" color="text.secondary">Seleziona attività</Typography>;
                                                }
                                                const activity = userActivities.find(a => a.activityId === value);
                                                if (!activity) return null;
                                                return (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <BusinessIcon sx={{ color: '#8B6F47', fontSize: '18px' }} />
                                                        <Box>
                                                            <Typography variant="body2" fontWeight="600" sx={{ fontSize: '0.85rem' }}>
                                                                {activity.indirizzo}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                                {activity.citta}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                );
                                            }}
                                        >
                                            {userActivities.length === 0 ? (
                                                <MenuItem value="" disabled>Nessuna attività</MenuItem>
                                            ) : (
                                                userActivities.map((activity) => (
                                                    <MenuItem key={activity.activityId} value={activity.activityId}>
                                                        <Box>
                                                            <Typography variant="body2" fontWeight="600">
                                                                {activity.indirizzo}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {activity.citta} ({activity.provincia})
                                                            </Typography>
                                                        </Box>
                                                    </MenuItem>
                                                ))
                                            )}
                                        </Select>
                                    </FormControl>
                                </Box>
                            )}
                        </Box>
                    </>
                )}
                
                {/* Utenti normali: Dropdown attività */}
                {!isAdmin && (
                    <>
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 0.5,
                    backgroundColor: 'rgba(0, 0, 0, 0.15)',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    alignSelf: 'flex-start',
                    ml: 'auto',
                    mr: 'auto'
                }}>
                    <BusinessIcon sx={{ fontSize: '16px', color: 'white' }} />
                    <Typography variant="caption" sx={{ 
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        letterSpacing: '0.3px'
                    }}>
                        Seleziona Attività
                    </Typography>
                </Box>
                <FormControl size="small" sx={{ 
                    backgroundColor: 'white', 
                    borderRadius: 1.5,
                    width: '100%',
                    maxWidth: 550,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                    transition: 'all 0.3s ease',
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                            borderColor: 'rgba(139, 111, 71, 0.5)',
                            borderWidth: '1.5px',
                        },
                        '&:hover fieldset': {
                            borderColor: 'rgba(139, 111, 71, 0.8)',
                            borderWidth: '2px',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#8B6F47',
                            borderWidth: '2px',
                        },
                    },
                }}>
                    <Select
                        value={selectedActivity}
                        onChange={(e) => handleActivityChange(e.target.value)}
                        disabled={activities.length === 0}
                        displayEmpty
                        renderValue={(value) => {
                            if (!value) {
                                if (activities.length === 0) {
                                    return (
                                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                                            Nessuna attività disponibile
                                        </Typography>
                                    );
                                }
                                return (
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                                        Seleziona un'attività
                                    </Typography>
                                );
                            }
                            const selected = activities.find(a => a.activityId === value);
                            if (!selected) return null;
                            return (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'center' }}>
                                    <LocationOnIcon sx={{ color: '#8B6F47', fontSize: '20px' }} />
                                    <Box>
                                        <Typography variant="body2" fontWeight="600" sx={{ color: '#333', fontSize: '0.95rem', textAlign: 'center' }}>
                                            {selected.indirizzo}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem', textAlign: 'center' }}>
                                            {selected.citta} ({selected.provincia})
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        }}
                        sx={{
                            '& .MuiSelect-select': {
                                paddingY: 1.5,
                                paddingX: 2.5,
                                fontSize: '0.95rem'
                            }
                        }}
                    >
                        {activities.length === 0 ? (
                            <MenuItem value="" disabled>
                                Nessuna attività disponibile
                            </MenuItem>
                        ) : (
                            activities.map((activity) => (
                                <MenuItem 
                                    key={activity.activityId} 
                                    value={activity.activityId}
                                    sx={{
                                        py: 2,
                                        px: 2.5,
                                        minHeight: '60px',
                                        borderBottom: '1px solid rgba(0,0,0,0.06)',
                                        '&:last-child': {
                                            borderBottom: 'none',
                                        },
                                        '&:hover': {
                                            backgroundColor: 'rgba(139, 111, 71, 0.05)',
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: 'rgba(139, 111, 71, 0.12)',
                                            '&:hover': {
                                                backgroundColor: 'rgba(139, 111, 71, 0.15)',
                                            }
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, width: '100%', justifyContent: 'center' }}>
                                        <LocationOnIcon sx={{ color: '#8B6F47', fontSize: '22px', mt: 0.3 }} />
                                        <Box sx={{ flex: 1, textAlign: 'center' }}>
                                            <Typography variant="body2" fontWeight="600" sx={{ color: '#333', mb: 0.3, fontSize: '0.95rem' }}>
                                                {activity.indirizzo}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#666', fontSize: '0.8rem' }}>
                                                {activity.citta} ({activity.provincia}) · CAP {activity.cap}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </MenuItem>
                            ))
                        )}
                    </Select>
                </FormControl>
                    </>
                )}
            </Grid>
            <Grid item xs={3} style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <CalendarMonthIcon style={{ fontSize: "28px", color: "#ffffff" }}></CalendarMonthIcon>
                    <div style={{ display: "flex", flexDirection: "column", lineHeight: "1.2" }}>
                        <Typography variant="body2" color="#ffffff" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                            {currentDayOfWeek}
                        </Typography>
                        <Typography variant="caption" color="rgba(255,255,255,0.85)" sx={{ fontSize: '0.75rem' }}>
                            {currentDayOfMonth}
                        </Typography>
                    </div>
                </div>
                <div style={{ width: '1px', height: '36px', backgroundColor: 'rgba(255,255,255,0.5)' }}></div>
                <Box 
                    onClick={handleMenuOpen}
                    sx={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: "8px",
                        cursor: 'pointer',
                        padding: '6px 12px',
                        borderRadius: '8px',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.15)'
                        }
                    }}
                >
                    <div style={{ display: "flex", flexDirection: "column", lineHeight: "1.3", alignItems: "flex-end" }}>
                        {ragioneSociale && (
                            <Typography variant="body2" color="#ffffff" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                {ragioneSociale}
                            </Typography>
                        )}
                        <Typography variant="caption" color="rgba(255,255,255,0.85)" sx={{ fontSize: '0.75rem' }}>
                            {loggedUser}
                        </Typography>
                    </div>
                    <KeyboardArrowDownIcon 
                        sx={{ 
                            fontSize: '24px', 
                            color: 'white',
                            transform: openMenu ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s ease'
                        }} 
                    />
                </Box>
                <Menu
                    anchorEl={anchorEl}
                    open={openMenu}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    PaperProps={{
                        sx: {
                            mt: 1.5,
                            minWidth: 240,
                            borderRadius: 2,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            '& .MuiMenuItem-root': {
                                px: 2,
                                py: 1.5,
                                borderRadius: 1,
                                mx: 1,
                                '&:hover': {
                                    backgroundColor: 'rgba(32, 103, 159, 0.08)'
                                }
                            }
                        }
                    }}
                >
                    <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                            {ragioneSociale || 'Utente'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                            {loggedUser}
                        </Typography>
                        {partitaIva && (
                            <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 0.5 }}>
                                P.IVA: {partitaIva}
                            </Typography>
                        )}
                    </Box>
                    <MenuItem onClick={() => { handleMenuClose(); setOpenUserDialog('profile'); }}>
                        <ListItemIcon>
                            <AccountCircleIcon sx={{ color: '#8B6F47', fontSize: '22px' }} />
                        </ListItemIcon>
                        <ListItemText>Il mio profilo</ListItemText>
                    </MenuItem>
                    <Divider sx={{ my: 1 }} />
                    <MenuItem onClick={() => { handleMenuClose(); setOpenUserDialog('support'); }}>
                        <ListItemIcon>
                            <HelpOutlineIcon sx={{ color: '#8B6F47', fontSize: '22px' }} />
                        </ListItemIcon>
                        <ListItemText>Supporto</ListItemText>
                    </MenuItem>
                    <Divider sx={{ my: 1 }} />
                    <MenuItem onClick={handleLogout}>
                        <ListItemIcon>
                            <LogoutIcon sx={{ color: '#d32f2f', fontSize: '22px' }} />
                        </ListItemIcon>
                        <ListItemText sx={{ color: '#d32f2f' }}>Disconnetti</ListItemText>
                    </MenuItem>
                </Menu>
                <Tooltip title={isAdmin ? 'Richieste di supporto' : 'Notifiche'}>
                    <Box
                        onClick={() => isAdmin && navigate('/admin-support')}
                        sx={{ position: 'relative', display: 'inline-flex', cursor: isAdmin ? 'pointer' : 'default' }}
                    >
                        <CircleNotificationsIcon sx={{ fontSize: 32, color: '#ffffff' }} />
                        {isAdmin && (supportCounts.open > 0 || supportCounts.inProgress > 0) && (
                            <Box sx={{ position: 'absolute', top: 26, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                {supportCounts.open > 0 && (
                                    <Badge
                                        badgeContent={supportCounts.open}
                                        onClick={(event) => { event.stopPropagation(); navigate('/admin-support'); }}
                                        aria-label={`${supportCounts.open} richieste aperte`}
                                        sx={{
                                            position: 'static',
                                            cursor: 'pointer',
                                            '& .MuiBadge-badge': { position: 'static', transform: 'none', backgroundColor: '#0288d1', color: '#fff', fontSize: 10, fontWeight: 700, minWidth: 17, height: 17, border: '2px solid #20679F', boxShadow: '0 1px 3px rgba(0,0,0,0.25)' }
                                        }}
                                    />
                                )}
                                {supportCounts.inProgress > 0 && (
                                    <Badge
                                        badgeContent={supportCounts.inProgress}
                                        onClick={(event) => { event.stopPropagation(); navigate('/admin-support'); }}
                                        aria-label={`${supportCounts.inProgress} richieste in lavorazione`}
                                        sx={{
                                            position: 'static',
                                            cursor: 'pointer',
                                            '& .MuiBadge-badge': { position: 'static', transform: 'none', backgroundColor: '#ed6c02', color: '#fff', fontSize: 10, fontWeight: 700, minWidth: 17, height: 17, border: '2px solid #20679F', boxShadow: '0 1px 3px rgba(0,0,0,0.25)' }
                                        }}
                                    />
                                )}
                            </Box>
                        )}
                    </Box>
                </Tooltip>
            </Grid>
        </Grid>
        <Dialog
            open={openUserDialog !== null}
            onClose={() => setOpenUserDialog(null)}
            fullWidth
            maxWidth={openUserDialog === 'support' ? 'lg' : 'sm'}
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    overflow: 'hidden'
                }
            }}
        >
            <DialogTitle sx={{ py: 2, px: 3, fontWeight: 600 }}>
                {openUserDialog === 'profile' ? 'Il mio profilo' : 'Supporto'}
                <IconButton
                    aria-label="Chiudi"
                    onClick={() => setOpenUserDialog(null)}
                    sx={{ position: 'absolute', right: 12, top: 12 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: { xs: 2, sm: 3 }, backgroundColor: '#fafafa' }}>
                {openUserDialog === 'profile' ? <Profile /> : <Support />}
            </DialogContent>
        </Dialog>
        <Backdrop
            sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
            open={isLoggingOut}
        >
            <CircularProgress color="inherit" />
        </Backdrop>
        </>
    )
}

